import React, { useCallback, useEffect, useState } from 'react';
import type { CatalogItem, CatalogListResponse, Genre } from '@lumos/contracts';
import { Card } from '../components/Card.js';
import { useInfiniteScroll } from '../components/useInfiniteScroll.js';
import { GridSkeleton, RetryError } from '../components/Placeholders.js';
import { SectionWash, useWashItem } from '../components/SectionWash.js';
import { LumosMark } from '../components/icons.js';


interface CatalogListPageProps {
  title: string;
  icon: React.ReactNode;
  emptyDescription: string;
  fetchPage: (page: number, signal?: AbortSignal, genre?: string) => Promise<CatalogListResponse>;
}

/**
 * Постраничный каталог для `#/movies` и `#/tv`: разделяет фильмы и сериалы,
 * которые раньше были смешаны в секциях главной страницы.
 *
 * Над списком — ряд жанров. Выбор жанра уходит на сервер и меняет запрос к
 * витрине, а не прячет уже полученные карточки: иначе из двадцати позиций
 * страницы на экране осталось бы две-три.
 */
export function CatalogListPage({
  title,
  icon,
  emptyDescription,
  fetchPage,
}: CatalogListPageProps): React.JSX.Element {
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [genres, setGenres] = useState<Genre[]>([]);
  const [genre, setGenre] = useState<string | null>(null);
  /** Попытка загрузки: «Повторить» после ошибки заводит её заново. */
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setItems([]);
    setPage(0);
    setTotalPages(0);
    setNotice(null);
    setError(null);
    setLoading(true);
    fetchPage(1, controller.signal, genre ?? undefined)
      .then((response) => {
        setItems(response.items);
        setPage(response.page);
        setTotalPages(response.totalPages);
        setNotice(response.notice);
        // Список жанров переносим из ответа только если он непустой: при
        // недоступной витрине ответ приходит без жанров, и ряд кнопок не
        // должен исчезать вместе с ним.
        if (response.genres.length > 0) setGenres(response.genres);
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted) return;
        setError(e instanceof Error ? e.message : 'Ошибка загрузки');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [fetchPage, genre, attempt]);

  const loadMore = useCallback((): void => {
    setLoadingMore(true);
    fetchPage(page + 1, undefined, genre ?? undefined)
      .then((response) => {
        setItems((prev) => [...prev, ...response.items]);
        setPage(response.page);
        setTotalPages(response.totalPages);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Ошибка загрузки'))
      .finally(() => setLoadingMore(false));
  }, [fetchPage, page, genre]);

  const canLoadMore = !loading && page > 0 && page < totalPages;
  const sentinelRef = useInfiniteScroll(loadMore, {
    enabled: canLoadMore && !loadingMore,
    rearmKey: items.length,
  });

  const { washItem, onFocusItem, onLeaveItem } = useWashItem(items);

  return (
    <main className="app">
      <SectionWash item={washItem} />
      <header className="header">
        <span className="brand">
          <LumosMark className="brand-mark" />
          Lumos
        </span>
        {/* Название раздела стоит В шапке, на одной строке с логотипом: своей
            строкой оно занимало высоту, ничего к ней не добавляя, и отодвигало
            жанры с постерами вниз. */}
        <h1 className="page-title header-title">{title}</h1>
      </header>

      {genres.length > 0 && (
        <div className="genre-row" role="group" aria-label={`Жанры: ${title}`}>
          {/* «Все» — способ вернуться из жанра, не нажимая выбранную кнопку
              повторно: возврат должен быть очевиден, а не угадываться. */}
          <button
            type="button"
            className={`genre-chip${genre === null ? ' is-active' : ''}`}
            aria-pressed={genre === null}
            onClick={() => setGenre(null)}
          >
            Все
          </button>
          {genres.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`genre-chip${genre === item.id ? ' is-active' : ''}`}
              aria-pressed={genre === item.id}
              onClick={() => setGenre(item.id)}
            >
              {item.name}
            </button>
          ))}
        </div>
      )}

      {error && <RetryError message={error} onRetry={() => setAttempt((value) => value + 1)} />}
      {loading && !error && <GridSkeleton />}
      {notice && !loading && <p className="catalog-notice">{notice}</p>}

      {!loading && !error && items.length === 0 && (
        <div className="empty-state" role="status">
          <div className="empty-state-icon" aria-hidden="true">
            {icon}
          </div>
          <h2>{title}</h2>
          <p>{genre === null ? emptyDescription : 'В этом жанре ничего не нашлось.'}</p>
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="grid">
            {items.map((item) => (
              <Card key={item.id} item={item} onFocusItem={onFocusItem} onLeaveItem={onLeaveItem} />
            ))}
          </div>

          {/* Маркер бесконечной подгрузки: список растёт по мере прокрутки. */}
          {canLoadMore && <div ref={sentinelRef} className="scroll-sentinel" aria-hidden="true" />}
          {loadingMore && <GridSkeleton count={6} />}
        </>
      )}
    </main>
  );
}
